var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CircularProgress from '../Progress/CircularProgress';
import { logErrorIfInvalidProp } from '../utils/errorHandling';
const validTypes = ['interactiveNeutral', 'interactiveSubtle', 'interactiveEmphasis'];
const getBaseStyles = (theme, type, disabled, selected) => {
    return Object.assign(Object.assign({ backgroundColor: selected
            ? theme.palette.button.interactive[type].background.selected.default
            : theme.palette.button.interactive[type].background.default, border: selected
            ? `${theme.palette.button.borderWidth} solid ${theme.palette.button.interactive[type].border.selected.default}`
            : `${theme.palette.button.borderWidth} solid ${theme.palette.button.interactive[type].border.default}`, color: theme.palette.button.interactive[type].text, hover: {
            backgroundColor: selected
                ? theme.palette.button.interactive[type].background.selected.hover
                : theme.palette.button.interactive[type].background.hover,
            border: selected
                ? `${theme.palette.button.borderWidth} solid ${theme.palette.button.interactive[type].border.selected.hover}`
                : `${theme.palette.button.borderWidth} solid ${theme.palette.button.interactive[type].border.hover}`
        }, focus: {
            backgroundColor: theme.palette.button.interactive[type].background.focus,
            border: `${theme.palette.button.borderWidth} solid ${theme.palette.button.interactive[type].border.focus}`
        } }, (disabled && {
        backgroundColor: theme.palette.button.interactive[type].background.disabled,
        border: `${theme.palette.button.borderWidth} solid ${theme.palette.button.interactive[type].border.disabled}`,
        opacity: 1
    })), { icon: {
            color: disabled
                ? theme.palette.button.interactive[type].icon.disabled
                : theme.palette.button.interactive[type].icon.default
        } });
};
const getButtonStyles = (theme, bdsType, disabled, selected) => {
    // If bdsType is invalid, log the error and return undefined
    if (bdsType && !validTypes.includes(bdsType)) {
        logErrorIfInvalidProp('Button', 'bdsType', bdsType);
        return undefined;
    }
    // Switch for valid bdsType values
    switch (bdsType) {
        case 'interactiveNeutral':
            return getBaseStyles(theme, 'neutral', disabled, selected);
        case 'interactiveSubtle':
            return getBaseStyles(theme, 'subtle', disabled, selected);
        case 'interactiveEmphasis':
            return getBaseStyles(theme, 'emphasis', disabled, selected);
        default:
            // This case shouldn't happen because we've already handled invalid types
            // But keeping it just in case to catch any future edge cases
            logErrorIfInvalidProp('Button', 'bdsType', bdsType);
            return undefined;
    }
};
const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => !['bdsType', 'selected'].includes(prop)
})(({ theme, bdsType = 'interactiveEmphasis', disabled, selected }) => {
    var _a, _b;
    const buttonStyles = bdsType ? getButtonStyles(theme, bdsType, disabled, selected) : undefined;
    return Object.assign(Object.assign({ borderRadius: theme.palette.button.radius, padding: theme.palette.button.padding, height: theme.palette.button.height, fontFamily: theme.palette.button.font.family, fontSize: theme.palette.button.font.size, fontWeight: theme.palette.button.font.weight, lineHeight: theme.palette.button.font.lineHeight, letterSpacing: theme.palette.button.font.letterSpacing, textTransform: 'none', gap: theme.palette.button.spaceBetween }, (buttonStyles && Object.assign(Object.assign({ backgroundColor: buttonStyles.backgroundColor, border: buttonStyles.border, color: buttonStyles.color }, (buttonStyles.hover && {
        '&:hover': {
            backgroundColor: buttonStyles.hover.backgroundColor,
            border: buttonStyles.hover.border
        }
    })), (buttonStyles.focus && {
        '&:focus': {
            backgroundColor: buttonStyles.focus.backgroundColor,
            border: buttonStyles.focus.border
        }
    })))), { 
        // Gap is set above
        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            margin: 0,
            fill: (_b = (_a = getButtonStyles(theme, bdsType, disabled)) === null || _a === void 0 ? void 0 : _a.icon) === null || _b === void 0 ? void 0 : _b.color
        } });
});
// Need to use forwardRef to pass ref to the underlying button element
// For components like Tooltip, Popover, etc. that use the ref prop to attach the element to the anchor
const BDSButton = React.forwardRef((_a, ref) => {
    var { children, disabled, isLoading = false, bdsType, startIcon, endIcon, selected = false } = _a, props = __rest(_a, ["children", "disabled", "isLoading", "bdsType", "startIcon", "endIcon", "selected"]);
    // Ensure props are being applied correctly
    return (React.createElement(StyledButton, Object.assign({ ref: ref }, props, { disabled: isLoading || disabled, bdsType: bdsType, startIcon: startIcon, endIcon: endIcon, selected: selected }),
        isLoading && (React.createElement(CircularProgress
        // TODO: Add size prop to CircularProgress
        , { 
            // TODO: Add size prop to CircularProgress
            size: 24, sx: {
                position: 'absolute'
            } })),
        children));
});
BDSButton.displayName = 'Button';
export default BDSButton;
//# sourceMappingURL=Button.js.map